<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { usePhoneStore } from '../../../stores';
import { closeModal, updateShow } from '../../../app/components/modal/modals';
import type { ModalProps } from '../../../app/components/modal/modals';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtModal } from '@dialpad/dialtone/vue3/lib/modal';
import { DATA_QA as MODAL_WRAPPER_DATA_QA } from '@/app/components/modal/modal-wrapper.constants';
import { DATA_QA } from './dnc-record-delete.constants';
import { useI18N } from '@dialpad/i18n';
import { showAutoDismissToast, showErrorToast } from '@/noto';
import logger from '@/logger';

const { $t } = useI18N();

const log = logger.getChild('DncRecordDelete');
log.debug('Mounting');

const props = defineProps<ModalProps>();

const phoneStore = usePhoneStore();
const mounted = ref(false);

const phone = phoneStore.watch(() => props.meta?.id ?? null, 'DncRecordDelete');

const confirm = async () => {
  log.debug('Confirm');
  if (!phone.value.item?.e164) {
    log.error('Phone e164 is missing');
    return;
  }

  void closeModal();
  log.debug('Deleting phone record', phone.value.item.e164);
  phoneStore.discard(phone.value.item.e164, {
    onSuccess: () => {
      void showAutoDismissToast({
        content: {
          title: $t('DNC_RECORD_DELETE_SUCCESS_TITLE'),
        },
      });
    },
    onRetry: () => {},
    onError: (error) => {
      log.log('Error deleting phone record', error);
      void showErrorToast({
        content: {
          title: $t('DNC_RECORD_DELETE_ERROR_TITLE'),
        },
      });
    },
  });
};

const cancel = () => {
  log.debug('Cancel');
  void closeModal();
};

onMounted(() => {
  log.debug('Mounted');
  mounted.value = true;

  if (!props.meta?.id) {
    log.error('Missing phone e164');
    void closeModal();
  }
});

const show = computed(() => mounted.value && !!phone.value.item);
</script>

<template>
  <DtModal
    :data-qa="MODAL_WRAPPER_DATA_QA.DIALOG"
    :title="$t('DNC_RECORD_DELETE_MODAL_TITLE')"
    :close-button-props="{
      ariaLabel: $t('MODAL_CLOSE_ARIA_LABEL'),
      'data-qa': MODAL_WRAPPER_DATA_QA.CLOSE,
    }"
    :show="show"
    content-class="d-p4 d-mn4"
    @update:show="updateShow"
  >
    {{ $t('DNC_RECORD_DELETE_DESCRIPTION', { phoneNumber: phone.item?.e164 }) }}
    <footer class="d-d-flex d-jc-flex-end d-mt16">
      <DtButton :data-qa="DATA_QA.CANCEL" importance="clear" @click="cancel">
        {{ $t('DNC_RECORD_DELETE_CANCEL') }}
      </DtButton>
      <DtButton
        :data-qa="DATA_QA.CONFIRM"
        kind="danger"
        importance="primary"
        class="d-ml6"
        @click="confirm"
      >
        {{ $t('DNC_RECORD_DELETE_CONFIRM') }}
      </DtButton>
    </footer>
  </DtModal>
</template>
